/* eslint-disable consistent-return */
import { Inline, Stack, Text, Column, textVariants, breakpoints, vars } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import type {
  BrandedFareFeature,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
} from '@eti/schema-types';
import { css } from '@eti/styles';
import { CheckCircle } from '@phosphor-icons/react';
import CabinBaggageFeature, { iconStyles } from './CabinBaggageFeature';
import PersonalItemAndCabinBaggageIncludedFeature from './PersonalItemAndCabinBaggageIncludedFeature';
import CheckedBaggageFeature from './CheckedBaggageFeature';
import isCheckedBaggageIncluded from './utils/isCheckedBaggageIncluded';
import isCabinBaggageIncluded from './utils/isCabinBaggageIncluded';

const featureListStyles = css`
  margin-top: 8px;
`;

const featureTextStyles = css`
  width: -webkit-fill-available; /* stylelint-disable */
`;

interface IncludedFeaturesProps {
  cabinBaggageTripSummary: CabinBaggageTripSummary;
  checkedBaggageSummary: CheckedBaggageSummary;
  isPersonalItemIncluded: boolean;
  includedFeatures?: BrandedFareFeature[];
}

const IncludedFeatures = ({
  cabinBaggageTripSummary,
  checkedBaggageSummary,
  isPersonalItemIncluded,
  includedFeatures,
}: IncludedFeaturesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const shouldUseCabinBaggageTextUsingAtpco = p('BrandedFares.CabinBaggage.From.Atpco.Enabled');
  const shouldShowPersonalItem = p('BrandedFares.ShowPersonalBaggage.Enabled');
  if (
    includedFeatures?.length === 0 &&
    !isCheckedBaggageIncluded(checkedBaggageSummary, t) &&
    !isCabinBaggageIncluded(cabinBaggageTripSummary, t)
  ) {
    return null;
  }
  return (
    <Column
      data-testid="included-features"
      width={{
        [breakpoints._0]: '4/4',
        [breakpoints._768]: '2/4',
      }}
    >
      <Text variant={textVariants._14_BOLD}>{t('PaymentPage.BrandedFare.Included.Title')}</Text>
      <Stack as="ul" className={featureListStyles} spacing={8}>
        {!shouldUseCabinBaggageTextUsingAtpco && shouldShowPersonalItem && (
          <PersonalItemAndCabinBaggageIncludedFeature
            cabinBaggageTripSummary={cabinBaggageTripSummary}
            isPersonalItemIncluded={isPersonalItemIncluded}
          />
        )}
        {!shouldUseCabinBaggageTextUsingAtpco && !shouldShowPersonalItem && (
          <CabinBaggageFeature cabinBaggageTripSummary={cabinBaggageTripSummary} />
        )}
        <CheckedBaggageFeature checkedBaggageSummary={checkedBaggageSummary} />
        {includedFeatures?.map((feature: BrandedFareFeature) => (
          <Inline as="li" key={feature.code} noWrap role="listitem" spacing={8}>
            <CheckCircle
              className={iconStyles}
              color={vars.colors.signal.positive.foreground}
              size={20}
              weight="fill"
            />
            <Text as="p" className={featureTextStyles}>
              {feature.name}
            </Text>
          </Inline>
        ))}
      </Stack>
    </Column>
  );
};
export default IncludedFeatures;
